import { isPlatformBrowser } from '@angular/common';
import {
  DOCUMENT,
  Injectable,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';

export type Theme = 'light' | 'dark';

export const THEMES: readonly Theme[] = ['light', 'dark'] as const;

const STORAGE_KEY = 'fu-theme';
const CLASS_PREFIX = 'theme-';

/**
 * Theme service - owns the active blueprint theme.
 *
 * Light is the default theme for every visitor. The OS
 * `prefers-color-scheme` is deliberately ignored: dark applies only when
 * the visitor switches to it via the site's theme switcher, and that
 * choice persists in localStorage (`fu-theme`). A persisted manual choice
 * always wins. The pre-paint script in `index.original.html` mirrors this
 * resolution before first paint so a returning dark-theme visitor gets no
 * light flash.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly doc = inject(DOCUMENT);

  private readonly themeState = signal<Theme>(this.detectInitialTheme());

  /** Reactive view of the current theme. */
  readonly theme = this.themeState.asReadonly();

  /** `theme-light` / `theme-dark` - convenient for templates. */
  readonly themeClass = computed(() => CLASS_PREFIX + this.themeState());

  constructor() {
    // The pre-paint script already applied the stored theme onto <html>;
    // this sync is a safety net keeping the class and service state
    // reconciled.
    this.applyToDom(this.themeState());
  }

  setTheme(theme: Theme): void {
    this.themeState.set(theme);
    this.applyToDom(theme);
    this.persist(theme);
  }

  cycleNext(): void {
    const idx = THEMES.indexOf(this.themeState());
    this.setTheme(THEMES[(idx + 1) % THEMES.length]);
  }

  private detectInitialTheme(): Theme {
    if (!isPlatformBrowser(this.platformId)) {
      return 'light';
    }
    return this.readStored() ?? 'light';
  }

  private readStored(): Theme | null {
    try {
      const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
      if (raw && (THEMES as readonly string[]).includes(raw)) {
        return raw as Theme;
      }
    } catch {
      /* private mode / blocked */
    }
    return null;
  }

  private persist(theme: Theme): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, theme);
    } catch {
      /* private mode / blocked */
    }
  }

  private applyToDom(theme: Theme): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const root = this.doc.documentElement;
    THEMES.forEach(t => root.classList.toggle(CLASS_PREFIX + t, t === theme));
  }
}
